import React from 'react';
import { TFunction } from 'react-i18next';
import {
  GridColDef,
  GridColumnHeaderParams,
  GridPreProcessEditCellProps,
  GridRenderCellParams,
  GridRenderEditCellParams,
} from '@mui/x-data-grid-pro';
import validationSchema, { nameValidationSchema } from './validationSchema';
import GenericGridContent from '../../../../components/datagridComponents/GenericGridContent';
import GridCellEdit from '../../../../components/datagridComponents/GridCellEdit';
import GridCellEditCheckbox from '../../../../components/datagridComponents/GridCellEditCheckbox';
import GridCellEditTextDropdown from '../../../../components/datagridComponents/GridCellEditTextDropdown';
import ActionsMenu from '../../../../components/datagridComponents/ActionsMenu';
import { Category } from '../../../../types';

const BuildColumns = ({
  t,
  categoriesData,
}: {
  t: TFunction;
  categoriesData: Category[];
}): GridColDef[] => {
  const categoriesAvailable = categoriesData?.map((row) => ({
    label: row.Name,
    value: row.Id?.toString(),
  }));

  return [
    {
      field: 'Name',
      flex: 0.5,
      minWidth: 100,
      editable: false,
      sortable: false,
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) =>
        validationSchema
          .isValid({
            Name: params?.props?.value,
          })
          .then((valid) => ({ ...params.props, error: !valid }))
          .catch(() => ({ ...params.props })),
      renderHeader: (_params: GridColumnHeaderParams) => (
        <GenericGridContent
          content={[
            {
              text: 'screens.products.categories.name',
              shouldTranslate: true,
              typographyProps: {
                variant: 'boldText',
              },
            },
          ]}
        />
      ),
      renderCell: (params: GridRenderCellParams) => (
        <GenericGridContent
          content={[
            {
              text: params.value,
              shouldTranslate: false,
              typographyProps: {
                variant: 'body2',
              },
            },
          ]}
        />
      ),
    },
    {
      field: 'IsWet',
      flex: 0.5,
      minWidth: 100,
      editable: false,
      sortable: false,
      renderHeader: (_params: GridColumnHeaderParams) => (
        <GenericGridContent
          content={[
            {
              text: t('screens.products.categories.wetDry'),
              shouldTranslate: true,
              typographyProps: {
                variant: 'boldText',
              },
            },
          ]}
        />
      ),
      renderCell: (params: GridRenderCellParams) => (
        <GenericGridContent
          content={[
            {
              text: params.value
                ? t('screens.products.categories.wet')
                : t('screens.products.categories.dry'),
              shouldTranslate: false,
              typographyProps: {
                variant: 'body2',
              },
            },
          ]}
        />
      ),
    },
    {
      field: 'ParentCategoryId',
      flex: 0.5,
      minWidth: 100,
      editable: false,
      sortable: false,
      renderHeader: (_params: GridColumnHeaderParams) => (
        <GenericGridContent
          content={[
            {
              text: t('screens.products.categories.parentCategory'),
              shouldTranslate: true,
              typographyProps: {
                variant: 'boldText',
              },
            },
          ]}
        />
      ),
      renderCell: (params: GridRenderCellParams) => (
        <GenericGridContent
          content={[
            {
              text: categoriesAvailable?.find(
                (category) => category?.value?.toString() === params.value?.toString()
              )?.label,
              shouldTranslate: false,
              typographyProps: {
                variant: 'body2',
              },
            },
          ]}
        />
      ),
    },
    {
      field: 'actions',
      minWidth: 100,
      editable: false,
      sortable: false,
      renderHeader: (_params: GridColumnHeaderParams) => (
        <GenericGridContent
          content={[
            {
              text: 'common.actions',
              shouldTranslate: true,
              typographyProps: {
                variant: 'boldText',
              },
            },
          ]}
        />
      ),
      align: 'right',
      headerAlign: 'right',
      hideSortIcons: true,
      headerClassName: 'hideRightSeparator',
      cellClassName: 'actionsCell',
      renderCell: (params: GridRenderCellParams) => (
        <ActionsMenu rowId={params.row.id.toString()} />
      ),
    },
  ];
};

export default BuildColumns;
